import {
  CheckSvg,
  color,
  Flex,
  PrimaryText,
  spacing,
  useColorMode,
  useToast,
} from 'deepstash-ui';
import React from 'react';
import useActivity from 'src/providers/hooks/useActivity';
import useAuth from 'src/providers/hooks/useAuth';
import useProfile from 'src/providers/hooks/useProfile';
import { Idea } from 'types/models';
import { getCollectionIdOnSourcePage } from 'utils/collection.utils';
import { TOAST_TIME } from 'utils/constants';
import { numberFormatter } from 'utils/global';
import useNsiActivity from 'providers/hooks/useNsiActivity';
import { getSingularOrPlural } from 'utils/StringUtils';

interface IdeaReadsLabelProps {
  idea: Idea;
}

const IdeaReadsLabel: React.FC<IdeaReadsLabelProps> = ({ idea }) => {
  const { colorMode } = useColorMode();
  const { isLoggedIn } = useAuth();
  const { activity, activityDispatch } = useActivity();
  const {
    nsiActivityDispatch,
    reads: nsiReads,
    getCanUseNsiActivity,
  } = useNsiActivity();
  const toast = useToast();
  const { id, totalReads } = idea;
  const { profile } = useProfile();

  const isIdeaReadInCurrSession =
    activity?.sessionReads?.has(id) || nsiReads.sessionReads.has(id);

  const readsDoneByCurrentUser = isLoggedIn
    ? // The user is logged in so we take the number of reads from the activity provider
      activity?.reads?.[id] ?? 0
    : // The user is NSI. We check if he read this idea during this session
    nsiReads.sessionReads.has(id)
    ? 1
    : 0;

  const onReadsLabelClick = (
    ev: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (isIdeaReadInCurrSession) {
      toast({
        id: 'idea-already-read',
        title: 'Idea read',
        description: 'You read this idea recently.',
        duration: TOAST_TIME,
        isClosable: true,
        customBackgroundColor: color[colorMode].top,
        customTextColor: color[colorMode].text,
        customIcon: (
          <CheckSvg
            color={color.success}
            width={spacing.XL.rem}
            height={spacing.XL.rem}
            marginRight={spacing.S.rem}
          />
        ),
      });
    } else {
      if (isLoggedIn) {
        activityDispatch({
          type: 'increment-read',
          payload: {
            ideaId: id,
            readingGoal: profile?.dailyReadingGoal ?? 0,
            collectionId: getCollectionIdOnSourcePage(),
          },
        });
      } else {
        nsiActivityDispatch({
          type: 'read-idea',
          payload: {
            ideaId: id,
          },
        });
      }
    }
  };

  return isLoggedIn || getCanUseNsiActivity() ? (
    <Flex px={spacing.XS.rem} onClick={onReadsLabelClick} align="center">
      <PrimaryText size="s" color={color[colorMode].textSecondary}>
        {numberFormatter(totalReads + readsDoneByCurrentUser)}
        {(activity?.reads?.[id] || nsiReads.sessionReads.has(id)) && (
          <PrimaryText
            display="inline"
            size="s"
            color={color[colorMode].primary.default}
            px={spacing.XXS.rem}
          >
            {`(+${readsDoneByCurrentUser})`}
          </PrimaryText>
        )}{' '}
        {getSingularOrPlural(
          totalReads + readsDoneByCurrentUser,
          'read',
          'reads',
        )}
      </PrimaryText>
    </Flex>
  ) : null;
};

export default IdeaReadsLabel;
